import { useEffect, useRef, useState } from 'react'
import {
  NOTIFICATION_BADGE_CAP,
  NOTIFICATIONS,
  type NotificationBellProps,
} from '../constants/notifications'
import { cx } from '../helpers/format'
import { NotificationList } from './NotificationList'
import '../styles/notifications.scss'

export function NotificationBell({
  items,
  unreadCount,
  loading,
  error,
  ringing,
  onOpenItem,
  onMarkAllRead,
}: NotificationBellProps) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const badge =
    unreadCount > NOTIFICATION_BADGE_CAP
      ? `${NOTIFICATION_BADGE_CAP}+`
      : String(unreadCount)

  useEffect(() => {
    if (!open) return
    function onPointerDown(event: PointerEvent) {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  return (
    <div className="notif-bell" ref={rootRef}>
      <button
        type="button"
        className={cx('icon-btn', ringing && 'icon-btn--ringing')}
        title={NOTIFICATIONS.bellLabel}
        aria-label={NOTIFICATIONS.bellLabel}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen((current) => !current)}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
          <path d="M6 9a6 6 0 1 1 12 0c0 7 3 7 3 7H3s3 0 3-7" />
          <path d="M10 19a2 2 0 0 0 4 0" />
        </svg>
        {unreadCount > 0 ? <span className="icon-btn__count">{badge}</span> : null}
      </button>
      {open ? (
        <div className="notif-bell__menu" role="dialog" aria-label={NOTIFICATIONS.menuTitle}>
          <div className="notif-bell__header">
            <p className="notif-bell__title">{NOTIFICATIONS.menuTitle}</p>
            {unreadCount > 0 ? (
              <button type="button" className="notif-bell__mark" onClick={onMarkAllRead}>
                {NOTIFICATIONS.markAll}
              </button>
            ) : null}
          </div>
          <NotificationList
            items={items}
            loading={loading}
            error={error}
            onOpen={(item) => {
              setOpen(false)
              onOpenItem(item)
            }}
          />
        </div>
      ) : null}
    </div>
  )
}
